import { ACCOUNT_MANAGEMENT_LE_ADMIN } from '../../../constants/roles';

import type { MemberRole } from './constants';

/**
 * Roles whose grant or revocation requires a step-up 2FA challenge. Mirrors
 * `organizations.StepUpGatedRoles` in identity/account-management, which
 * re-derives the requirement authoritatively; these values only decide when the
 * Workspace UI prompts.
 */
export const STEP_UP_GATED_ROLES: readonly MemberRole[] = [
  ACCOUNT_MANAGEMENT_LE_ADMIN,
  'account_management/legal_entity:viewer',
  'cdp/legal_entity:cdp_entity_admin_role',
  'shared/legal_entity:coinbiz_admin',
  // Frontend format, not the bare WORKSPACE_ORG_ADMIN in constants/roles.ts: this list is compared
  // against legalEntityRoles[], which carries frontend-format strings, while the bare name appears
  // on the top-level roles[] array that canManageMembers.ts reads. No Workspace UI grants the role
  // yet, so it cannot match today; it is listed so isGatedRoleMutation challenges as soon as one does.
  'account_management/organization:workspace_org_admin',
];

const GATED_ROLES = new Set<MemberRole>(STEP_UP_GATED_ROLES);

// Stands in for the one legal entity isSingleEntityGatedRoleMutation compares within.
const SINGLE_LEGAL_ENTITY = '';

export type LegalEntityRoles = {
  legalEntityId: string;
  roles: readonly MemberRole[];
};

export function hasGatedRole(roles: readonly MemberRole[]): boolean {
  return roles.some((role) => GATED_ROLES.has(role));
}

function setsAreEqual(current: Set<string>, next: Set<string>): boolean {
  if (current.size !== next.size) {
    return false;
  }
  for (const key of current) {
    if (!next.has(key)) {
      return false;
    }
  }
  return true;
}

function gatedRoleKeys(legalEntityRoles: readonly LegalEntityRoles[]): Set<string> {
  const keys = new Set<string>();
  for (const { legalEntityId, roles } of legalEntityRoles) {
    for (const role of roles) {
      if (GATED_ROLES.has(role)) {
        keys.add(JSON.stringify([legalEntityId, role]));
      }
    }
  }
  return keys;
}

/**
 * True when the submitted roles add or remove a gated role. Keyed by
 * (legalEntityId, role) so moving a gated role between legal entities counts as
 * both an add and a remove and still challenges.
 */
export function isGatedRoleMutation(
  currentLegalEntityRoles: readonly LegalEntityRoles[],
  nextLegalEntityRoles: readonly LegalEntityRoles[],
): boolean {
  return !setsAreEqual(gatedRoleKeys(currentLegalEntityRoles), gatedRoleKeys(nextLegalEntityRoles));
}

/** isGatedRoleMutation where both role sets belong to the same legal entity. */
export function isSingleEntityGatedRoleMutation(
  currentRoles: readonly MemberRole[],
  nextRoles: readonly MemberRole[],
): boolean {
  return isGatedRoleMutation(
    [{ legalEntityId: SINGLE_LEGAL_ENTITY, roles: currentRoles }],
    [{ legalEntityId: SINGLE_LEGAL_ENTITY, roles: nextRoles }],
  );
}
